import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  listProposals, duplicateProposal, deleteProposal, updateProposal, sendEmail,
} from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Plus, Search, MoreHorizontal, Copy, Trash2, FileText, Bell, Eye,
  TrendingUp, Mail, Clock, ChevronLeft, ChevronRight, X, Flame, Send,
  CheckCircle2, ChevronDown, Filter, Sparkles, ArrowRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "sonner";

/* ────────────────────────────────────────────────
   Helpers
   ──────────────────────────────────────────────── */
function timeAgo(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, now - then);
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min${m === 1 ? "" : "s"} ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hour${h === 1 ? "" : "s"} ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d} day${d === 1 ? "" : "s"} ago`;
  return new Date(iso).toLocaleDateString();
}
function fullDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString([], {
    month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}
function initials(name) {
  if (!name) return "—";
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("");
}
function formatValue(v) {
  if (!v) return "—";
  const s = String(v).trim();
  if (!s) return "—";
  // Try to extract a compact monetary token first
  const m = s.match(/\$\s?\d[\d,]*(?:\.\d+)?\s?(?:k|K|M|m)?(?:\s?\/\s?(?:yr|mo|month|year))?/);
  if (m) return m[0];
  // Fallback: first ~28 chars single-line
  return s.length > 28 ? s.slice(0, 28) + "…" : s;
}
function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

/* ────────────────────────────────────────────────
   Sparkline (7-day open trend)
   ──────────────────────────────────────────────── */
function Sparkline({ data }) {
  const max = Math.max(1, ...data);
  return (
    <svg viewBox="0 0 84 28" className="h-7 w-[84px]" aria-hidden="true">
      {data.map((v, i) => {
        const h = Math.max(2, (v / max) * 24);
        return (
          <rect
            key={i}
            x={i * 12}
            y={26 - h}
            width={8}
            height={h}
            rx={2}
            fill={v > 0 ? "#011B90" : "#E8EAF3"}
          />
        );
      })}
    </svg>
  );
}

/* ────────────────────────────────────────────────
   Stat card
   ──────────────────────────────────────────────── */
function StatCard({ label, value, delta, deltaColor = "muted", icon: Icon, sparkline, tid }) {
  const deltaClass =
    deltaColor === "green" ? "text-emerald-600" :
    deltaColor === "red" ? "text-red-600" :
    "text-[#9098A8]";
  return (
    <div
      data-testid={tid}
      className="qw-card p-5 hover:border-[#011B90]/40 transition-colors group"
    >
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <div className="font-display text-[30px] font-medium tracking-tight text-[#0B1736] leading-none">
            {value}
          </div>
          <div className="mt-2 text-[13px] text-[#5F6677] font-medium">{label}</div>
          {delta && (
            <div className={`mt-1 text-[12px] font-medium ${deltaClass}`}>{delta}</div>
          )}
        </div>
        <div className="flex flex-col items-end gap-2">
          {Icon && (
            <div
              className="h-9 w-9 rounded-full grid place-items-center shrink-0"
              style={{ background: "#EDEFF7", color: "#011B90" }}
            >
              <Icon size={16} />
            </div>
          )}
          {sparkline && <Sparkline data={sparkline} />}
        </div>
      </div>
      {/* purple hover underline */}
      <div className="h-[2px] mt-4 -mx-5 -mb-5 rounded-b-[12px] bg-transparent group-hover:bg-[#011B90] transition-colors" />
    </div>
  );
}

/* ────────────────────────────────────────────────
   Status Pill
   ──────────────────────────────────────────────── */
function StatusPill({ status }) {
  const s = status || "draft";
  const cls = {
    draft: "qw-status-draft",
    sent: "qw-status-sent",
    opened: "qw-status-opened",
    approved: "qw-status-approved",
    converted: "qw-status-converted",
    archived: "qw-status-archived",
  }[s] || "qw-status-draft";
  return (
    <span className={`qw-status-pill ${cls}`}>
      {s === "converted" && <CheckCircle2 size={11} />}
      <span className="capitalize">{s}</span>
    </span>
  );
}

/* ────────────────────────────────────────────────
   Follow-up compose modal
   ──────────────────────────────────────────────── */
function FollowUpModal({ proposal, onClose, onSent, senderFirstName = "there" }) {
  const [subject, setSubject] = useState(`Following up — ${proposal.company_name || proposal.title || "our proposal"}`);
  const [body, setBody] = useState(
    `Hi ${proposal.prospect_name ? proposal.prospect_name.split(" ")[0] : "there"},\n\n` +
    `Just checking in on the proposal I sent — happy to walk through anything or adjust scope where useful.\n\n` +
    `Let me know if a 15-min call this week works.\n\nBest,\n${senderFirstName}`
  );
  const [to, setTo] = useState(proposal.prospect_email || "");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const onEsc = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [onClose]);

  const send = async () => {
    if (!to.trim()) {
      toast.error("Add a recipient email");
      return;
    }
    setSending(true);
    try {
      await sendEmail(proposal.id, { to, subject, body });
      toast.success(`Follow-up sent to ${proposal.prospect_name || to}`);
      onSent?.();
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (err?.message || "Could not send follow-up");
      toast.error("Send failed", { description: msg, duration: 9000 });
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      data-testid="followup-modal"
      className="fixed inset-0 z-50 bg-[#0B1736]/40 backdrop-blur-sm flex items-center justify-center p-4 fade-in"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl border border-[#E6E8EE] w-full max-w-lg shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-[#E6E8EE] flex items-start justify-between">
          <div>
            <div className="text-[11px] tracking-[0.16em] uppercase font-semibold text-[#8A93A6]">
              Quick follow-up
            </div>
            <div className="text-[17px] font-semibold text-[#0B1736] mt-0.5">
              {proposal.company_name || proposal.title}
            </div>
          </div>
          <button
            data-testid="followup-close"
            onClick={onClose}
            className="h-8 w-8 grid place-items-center rounded-md hover:bg-[#F7F8FA] text-[#5F6677]"
          >
            <X size={16} />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wide text-[#8A93A6]">To</label>
            <Input
              data-testid="followup-to"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="prospect@company.com"
              className="mt-1 h-9 text-sm"
            />
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wide text-[#8A93A6]">Subject</label>
            <Input
              data-testid="followup-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="mt-1 h-9 text-sm"
            />
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wide text-[#8A93A6]">Message</label>
            <textarea
              data-testid="followup-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={7}
              className="mt-1 w-full rounded-md border border-[#E6E8EE] p-3 text-sm text-[#0B1736] focus:outline-none focus:border-[#0B1736]"
            />
          </div>
        </div>
        <div className="p-4 border-t border-[#E6E8EE] flex items-center justify-end gap-2 bg-[#F7F8FA] rounded-b-xl">
          <button
            data-testid="followup-cancel"
            onClick={onClose}
            className="px-3.5 h-9 rounded-lg text-sm text-[#5F6677] hover:bg-white"
          >
            Cancel
          </button>
          <button
            data-testid="followup-send"
            onClick={send}
            disabled={sending}
            className="qw-btn-primary px-4 h-9 flex items-center gap-2 disabled:opacity-60"
          >
            <Send size={14} />
            {sending ? "Sending…" : "Send follow-up"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────
   Hot proposal card
   ──────────────────────────────────────────────── */
function HotCard({ p, onFollow, onView }) {
  const opened = p.last_opened_at ? new Date(p.last_opened_at) : null;
  const glow = opened && Date.now() - opened.getTime() < 60 * 60 * 1000;
  return (
    <div
      data-testid={`hot-card-${p.id}`}
      className={`qw-card-hot ${glow ? "glow" : ""} shrink-0 w-[280px] p-4 flex flex-col gap-3`}
    >
      <div className="flex items-start gap-2.5">
        <div
          className="h-9 w-9 rounded-md grid place-items-center text-[12px] font-semibold shrink-0"
          style={{ background: "#0B1736", color: "#FFFFFF" }}
        >
          {initials(p.company_name)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[14px] font-semibold text-[#0B1736] truncate">
            {p.company_name || "—"}
          </div>
          <div className="text-[12px] text-[#5F6677] truncate">
            {p.prospect_name || "—"}{p.prospect_title ? ` · ${p.prospect_title}` : ""}
          </div>
        </div>
      </div>

      <div className="text-[12px] text-[#5F6677] space-y-1">
        <div className="flex items-center gap-1.5">
          <Eye size={12} className="text-[#011670]" />
          <span>Opened <b className="text-[#0B1736]">{p.open_count || 1}</b> {(p.open_count || 1) === 1 ? "time" : "times"}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Clock size={12} className="text-[#8A93A6]" />
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="cursor-default">Last seen: {timeAgo(p.last_opened_at)}</span>
              </TooltipTrigger>
              <TooltipContent>{fullDate(p.last_opened_at)}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      <div className="pt-1 border-t border-[#E6E8EE] text-[12px]">
        <div className="text-[#8A93A6] truncate">Proposal: {p.title || "—"}</div>
        <div className="mt-0.5 text-[#0B1736] font-semibold">{formatValue(p.total_value || p.pricing)}</div>
      </div>

      <div className="flex items-center gap-2">
        <button
          data-testid={`hot-follow-${p.id}`}
          onClick={() => onFollow(p)}
          className="qw-btn-primary flex-1 h-8 text-[12.5px] flex items-center justify-center gap-1.5"
        >
          <Send size={12} /> Follow Up
        </button>
        <button
          data-testid={`hot-view-${p.id}`}
          onClick={() => onView(p.id)}
          className="flex-1 h-8 text-[12.5px] rounded-lg border border-[#E6E8EE] text-[#0B1736] hover:border-[#0B1736] hover:bg-[#F7F8FA] transition-colors"
        >
          View Proposal
        </button>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────
   Main Dashboard
   ──────────────────────────────────────────────── */
export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("all");
  const [page, setPage] = useState(1);
  const [followUp, setFollowUp] = useState(null);
  const perPage = 10;
  const userName = (user?.name || "").trim().split(/\s+/)[0] || "there";

  const load = useCallback(async () => {
    try {
      const data = await listProposals();
      setItems(Array.isArray(data) ? data : []);
    } catch {
      setItems([]);
    }
  }, []);

  useEffect(() => {
    load();
    const poll = setInterval(load, 20000);
    const onKey = (e) => {
      if (e.key.toLowerCase() === "n" && document.activeElement?.tagName !== "INPUT" && document.activeElement?.tagName !== "TEXTAREA") {
        navigate("/new");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("keydown", onKey); clearInterval(poll); };
  }, [navigate, load]);

  /* ── Derived stats ── */
  const stats = useMemo(() => {
    if (!items) return null;
    const now = new Date();
    const total = items.length;
    const oneWeekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const createdThisWeek = items.filter((p) => p.created_at && new Date(p.created_at).getTime() > oneWeekAgo).length;

    const sentOrBeyond = items.filter((p) => ["sent", "opened", "approved", "converted"].includes(p.status));
    const sentThisMonth = sentOrBeyond.filter((p) => {
      const d = p.sent_at ? new Date(p.sent_at) : null;
      return d && d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    }).length;
    const sentLastMonth = sentOrBeyond.filter((p) => {
      const d = p.sent_at ? new Date(p.sent_at) : null;
      if (!d) return false;
      const lm = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      return d.getMonth() === lm.getMonth() && d.getFullYear() === lm.getFullYear();
    }).length;

    const opened = items.filter((p) => ["opened", "approved", "converted"].includes(p.status)).length;
    const openRate = sentOrBeyond.length ? Math.round((opened / sentOrBeyond.length) * 100) : 0;

    /* Avg response time — sent_at to opened_at diff in hours */
    const responseTimes = items
      .filter((p) => p.sent_at && p.opened_at)
      .map((p) => (new Date(p.opened_at).getTime() - new Date(p.sent_at).getTime()) / 3600000)
      .filter((n) => n >= 0 && n < 24 * 30);
    const avgResp = responseTimes.length
      ? (responseTimes.reduce((s, x) => s + x, 0) / responseTimes.length)
      : null;

    /* Sparkline: opens per day, last 7 days */
    const spark = Array.from({ length: 7 }, (_, i) => {
      const day = new Date();
      day.setHours(0, 0, 0, 0);
      day.setDate(day.getDate() - (6 - i));
      const next = new Date(day); next.setDate(next.getDate() + 1);
      return items.filter((p) => {
        const t = p.last_opened_at ? new Date(p.last_opened_at) : null;
        return t && t >= day && t < next;
      }).length;
    });

    const oneDay = 24 * 60 * 60 * 1000;
    const hot = items
      .filter((p) => p.last_opened_at && (now - new Date(p.last_opened_at)) < oneDay)
      .sort((a, b) => new Date(b.last_opened_at) - new Date(a.last_opened_at))
      .slice(0, 5);

    return { total, createdThisWeek, sentThisMonth, sentLastMonth, openRate, avgResp, spark, hot };
  }, [items]);

  const counts = useMemo(() => {
    if (!items) return {};
    const c = { all: items.length, draft: 0, sent: 0, opened: 0, approved: 0, converted: 0 };
    for (const p of items) {
      const s = p.status || "draft";
      if (s in c) c[s] += 1;
    }
    return c;
  }, [items]);

  const filtered = useMemo(() => {
    if (!items) return [];
    let r = items;
    if (tab !== "all") r = r.filter((p) => (p.status || "draft") === tab);
    if (q.trim()) {
      const t = q.toLowerCase();
      r = r.filter(
        (p) =>
          (p.company_name || "").toLowerCase().includes(t) ||
          (p.prospect_name || "").toLowerCase().includes(t) ||
          (p.title || "").toLowerCase().includes(t)
      );
    }
    return [...r].sort(
      (a, b) => new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0)
    );
  }, [items, tab, q]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const pageItems = filtered.slice((page - 1) * perPage, page * perPage);
  useEffect(() => { setPage(1); }, [tab, q]);

  /* Activity feed */
  const activity = useMemo(() => {
    if (!items) return [];
    const events = [];
    for (const p of items) {
      if (p.last_opened_at) {
        events.push({
          id: `${p.id}-o`, type: "opened",
          text: `${p.prospect_name || "Prospect"} opened your proposal`,
          sub: p.company_name || "",
          at: p.last_opened_at,
          proposalId: p.id,
        });
      }
      if (p.sent_at) {
        events.push({
          id: `${p.id}-s`, type: "sent",
          text: `Proposal sent to ${p.company_name || "prospect"}`,
          sub: p.title || "",
          at: p.sent_at,
          proposalId: p.id,
        });
      }
      if ((p.status || "draft") === "draft" && p.updated_at) {
        events.push({
          id: `${p.id}-d`, type: "draft",
          text: `Draft saved — ${p.company_name || p.title || "Untitled"}`,
          sub: "",
          at: p.updated_at,
          proposalId: p.id,
        });
      }
    }
    return events.sort((a, b) => new Date(b.at) - new Date(a.at)).slice(0, 10);
  }, [items]);

  /* Handlers */
  const onDuplicate = async (id) => {
    const p = await duplicateProposal(id);
    toast.success("Proposal duplicated");
    await load();
    navigate(`/proposals/${p.id}`);
  };
  const onDelete = async (id) => {
    await deleteProposal(id);
    toast.success("Proposal deleted");
    load();
  };
  const onSendReminder = async (p) => {
    setFollowUp(p);
  };

  const isEmpty = items !== null && items.length === 0;
  const isLoading = items === null;

  /* ────────── Render ────────── */
  return (
    <div className="min-h-screen flex">
      {/* Center + right panel */}
      <div className="flex-1 min-w-0">
        {/* Top bar */}
        <header
          data-testid="dashboard-topbar"
          className="sticky top-0 z-30 bg-[#F7F8FA]/85 backdrop-blur-md border-b border-[#E6E8EE]"
        >
          <div className="px-6 md:px-8 py-4 flex items-center justify-between gap-4">
            <h1 className="font-display text-[24px] font-medium tracking-tight text-[#0B1736] truncate">
              {isEmpty ? "Welcome to QuoWin" : `${greeting()}, ${userName} 👋`}
            </h1>
            <div className="flex items-center gap-2 shrink-0">
              <button
                data-testid="notifications-btn"
                className="h-9 w-9 grid place-items-center rounded-lg bg-white border border-[#E6E8EE] hover:border-[#0B1736] transition-colors text-[#5F6677] relative"
                onClick={() => toast("No new notifications", { description: "You're all caught up." })}
                aria-label="Notifications"
              >
                <Bell size={16} />
                {stats && stats.hot.length > 0 && (
                  <span
                    className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full ring-2 ring-white"
                    style={{ background: "#011B90" }}
                  />
                )}
              </button>
              <button
                data-testid="new-proposal-btn"
                onClick={() => navigate("/new")}
                className="qw-btn-primary h-9 px-4 flex items-center gap-1.5 text-[13.5px]"
              >
                <Plus size={15} strokeWidth={2.5} /> New Proposal
              </button>
            </div>
          </div>
        </header>

        <div className="px-6 md:px-8 py-6 fade-in">
          {/* Loading skeleton */}
          {isLoading && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="qw-skeleton h-[128px]" />
              ))}
            </div>
          )}

          {/* Empty state — welcome banner */}
          {isEmpty && (
            <section
              data-testid="welcome-banner"
              className="qw-card p-8 md:p-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 mb-6"
              style={{
                background: "linear-gradient(135deg, #EDEFF7 0%, #FFFFFF 60%)",
                border: "1px solid #E8EAF3",
              }}
            >
              <div className="flex-1 min-w-0">
                <div
                  className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-widest px-2.5 py-1 rounded-full mb-3"
                  style={{ background: "rgba(1, 27, 144,0.14)", color: "#011670" }}
                >
                  <Sparkles size={12} /> Welcome
                </div>
                <h2 className="font-display text-[28px] md:text-[36px] font-medium tracking-tight leading-tight text-[#0B1736]">
                  Welcome to QuoWin, {userName}.
                </h2>
                <p className="mt-1.5 text-[15px] text-[#5F6677]">
                  You&rsquo;re one proposal away from your next deal.
                </p>
              </div>
              <button
                data-testid="welcome-cta"
                onClick={() => navigate("/new")}
                className="qw-btn-primary h-11 px-5 text-[14px] flex items-center gap-2 shrink-0"
              >
                Create your first proposal <ArrowRight size={16} />
              </button>
            </section>
          )}

          {/* Stat cards */}
          {!isLoading && !isEmpty && stats && (
            <section data-testid="stats-row" className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <StatCard
                tid="stat-total"
                label="Total proposals"
                value={stats.total}
                delta={stats.createdThisWeek > 0 ? `+${stats.createdThisWeek} this week` : "No new this week"}
                deltaColor={stats.createdThisWeek > 0 ? "green" : "muted"}
                icon={FileText}
              />
              <StatCard
                tid="stat-sent-month"
                label="Sent this month"
                value={stats.sentThisMonth}
                delta={`vs ${stats.sentLastMonth} last month`}
                deltaColor="muted"
                icon={Mail}
              />
              <StatCard
                tid="stat-open-rate"
                label="Open rate"
                value={`${stats.openRate}%`}
                delta="Industry avg 45%"
                deltaColor={stats.openRate >= 45 ? "green" : "muted"}
                icon={TrendingUp}
                sparkline={stats.spark}
              />
              <StatCard
                tid="stat-response"
                label="Prospect response time"
                value={stats.avgResp !== null ? `${stats.avgResp.toFixed(1)} hrs` : "—"}
                delta="From send to first open"
                deltaColor="muted"
                icon={Clock}
              />
            </section>
          )}

          {/* Hot proposals */}
          {!isLoading && !isEmpty && (
            <section data-testid="hot-section" className="mb-8">
              <div className="flex items-end justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2 font-display text-[18px] font-medium text-[#0B1736]">
                    <Flame size={16} className="text-[#011B90]" fill="#011B90" /> Hot right now
                  </div>
                  <div className="text-[12.5px] text-[#5F6677] mt-0.5">
                    Prospects who opened in the last 24 hours — follow up while you&rsquo;re top of mind
                  </div>
                </div>
                {stats && stats.hot.length > 0 && (
                  <div data-testid="hot-live-indicator" className="flex items-center gap-1.5 text-[11px] tracking-widest uppercase font-semibold text-[#011670]">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#011B90] animate-pulse" />
                    {stats.hot.length} live
                  </div>
                )}
              </div>

              {stats && stats.hot.length > 0 ? (
                <div
                  data-testid="hot-scroll"
                  className="flex gap-3 overflow-x-auto no-scrollbar -mx-1 px-1 pb-2"
                >
                  {stats.hot.map((p) => (
                    <HotCard
                      key={p.id}
                      p={p}
                      onFollow={(pp) => setFollowUp(pp)}
                      onView={(id) => navigate(`/proposals/${id}`)}
                    />
                  ))}
                </div>
              ) : (
                <div
                  data-testid="hot-empty"
                  className="qw-card p-6 text-center"
                >
                  <div className="text-[13.5px] text-[#5F6677]">
                    No hot proposals yet. Send your first proposal to start tracking.
                  </div>
                </div>
              )}
            </section>
          )}

          {/* Pipeline */}
          {!isLoading && !isEmpty && (
            <section id="pipeline-section" data-testid="pipeline-section" className="qw-card overflow-hidden">
              <div className="px-5 pt-4 pb-3 flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                <div>
                  <h2 className="font-display text-[19px] font-medium text-[#0B1736]">Your pipeline</h2>
                  <p className="text-[12.5px] text-[#5F6677] mt-0.5">
                    Every proposal, every stage, one place.
                  </p>
                </div>
                <div className="relative w-full md:w-72">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A93A6]" />
                  <Input
                    data-testid="pipeline-search"
                    placeholder="Search prospect or company"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    className="pl-9 h-9 text-[13px] bg-white border-[#E6E8EE]"
                  />
                </div>
              </div>

              {/* Tabs */}
              <div className="px-5 border-b border-[#E6E8EE] flex items-center gap-0.5 overflow-x-auto no-scrollbar">
                {[
                  { k: "all", label: "All" },
                  { k: "draft", label: "Draft" },
                  { k: "sent", label: "Sent" },
                  { k: "opened", label: "Opened" },
                  { k: "approved", label: "Approved" },
                  { k: "converted", label: "Converted" },
                ].map((t) => {
                  const active = tab === t.k;
                  return (
                    <button
                      key={t.k}
                      data-testid={`tab-${t.k}`}
                      onClick={() => setTab(t.k)}
                      className={`relative px-3.5 py-3 text-[13px] whitespace-nowrap transition-colors ${
                        active ? "text-[#0B1736] font-semibold" : "text-[#5F6677] hover:text-[#0B1736]"
                      }`}
                    >
                      {t.label}
                      <span className={`ml-1.5 text-[11px] ${active ? "text-[#011670]" : "text-[#8A93A6]"}`}>
                        ({counts[t.k] ?? 0})
                      </span>
                      {active && (
                        <span
                          className="absolute left-2 right-2 bottom-0 h-[2px]"
                          style={{ background: "#011B90" }}
                        />
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Table (desktop) */}
              <div className="hidden md:block">
                <div className="grid grid-cols-[minmax(220px,1.4fr)_minmax(160px,1fr)_140px_120px_140px_90px_50px] text-[11px] uppercase tracking-[0.14em] font-semibold text-[#8A93A6] px-5 py-3 border-b border-[#E6E8EE] bg-[#FAFBFF]">
                  <div>Prospect</div>
                  <div>Company</div>
                  <div>Value</div>
                  <div>Status</div>
                  <div>Last activity</div>
                  <div>Opens</div>
                  <div></div>
                </div>

                {pageItems.length === 0 && (
                  <PipelineEmpty onNew={() => navigate("/new")} />
                )}

                {pageItems.map((p) => (
                  <div
                    key={p.id}
                    data-testid={`pipeline-row-${p.id}`}
                    onClick={() => navigate(`/proposals/${p.id}`)}
                    className="qw-row grid grid-cols-[minmax(220px,1.4fr)_minmax(160px,1fr)_140px_120px_140px_90px_50px] items-center px-5 py-3.5 border-b border-[#F0F2F5] cursor-pointer"
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div
                        className="h-8 w-8 rounded-full grid place-items-center text-[11px] font-semibold shrink-0"
                        style={{ background: "#EDEFF7", color: "#0B1736" }}
                      >
                        {initials(p.prospect_name)}
                      </div>
                      <div className="min-w-0">
                        <div className="text-[13.5px] font-medium text-[#0B1736] truncate">
                          {p.prospect_name || "—"}
                        </div>
                        <div className="text-[11.5px] text-[#8A93A6] truncate">
                          {p.prospect_email || (p.prospect_title || "")}
                        </div>
                      </div>
                    </div>
                    <div className="min-w-0">
                      <div className="text-[13px] text-[#0B1736] truncate">{p.company_name || "—"}</div>
                    </div>
                    <div className="text-[13px] font-semibold text-[#0B1736] truncate" title={p.total_value || p.pricing || ""}>{formatValue(p.total_value || p.pricing)}</div>
                    <div><StatusPill status={p.status || "draft"} /></div>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="text-[12.5px] text-[#5F6677] cursor-default">
                            {timeAgo(p.updated_at || p.sent_at || p.created_at)}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>{fullDate(p.updated_at || p.sent_at || p.created_at)}</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    <div className="flex items-center gap-1 text-[13px] text-[#0B1736]">
                      <Eye size={13} className="text-[#8A93A6]" /> {p.open_count || 0}
                    </div>
                    <div onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            data-testid={`row-menu-${p.id}`}
                            className="h-7 w-7 grid place-items-center rounded-md hover:bg-[#F0F2F5] text-[#5F6677]"
                          >
                            <MoreHorizontal size={16} />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/proposals/${p.id}`)}>
                            <FileText size={13} className="mr-2" /> View
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate(`/proposals/${p.id}`)}>
                            <FileText size={13} className="mr-2" /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => onDuplicate(p.id)}>
                            <Copy size={13} className="mr-2" /> Duplicate
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => onSendReminder(p)}>
                            <Send size={13} className="mr-2" /> Send reminder
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => onDelete(p.id)} className="text-red-600 focus:text-red-700">
                            <Trash2 size={13} className="mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                ))}
              </div>

              {/* Card list (mobile) */}
              <div className="md:hidden">
                {pageItems.length === 0 && <PipelineEmpty onNew={() => navigate("/new")} />}
                {pageItems.map((p) => (
                  <div
                    key={p.id}
                    data-testid={`pipeline-mcard-${p.id}`}
                    onClick={() => navigate(`/proposals/${p.id}`)}
                    className="p-4 border-b border-[#F0F2F5] cursor-pointer active:bg-[#FAFBFF]"
                  >
                    <div className="flex items-center gap-2 justify-between">
                      <div className="text-[14px] font-semibold text-[#0B1736] truncate">
                        {p.prospect_name || "—"}
                      </div>
                      <StatusPill status={p.status || "draft"} />
                    </div>
                    <div className="text-[12.5px] text-[#5F6677] truncate">
                      {p.company_name || "—"}
                    </div>
                    <div className="mt-2 flex items-center justify-between text-[12.5px]">
                      <span className="font-semibold text-[#0B1736]">{formatValue(p.total_value || p.pricing)}</span>
                      <span className="text-[#8A93A6]">
                        <Eye size={11} className="inline mr-0.5" /> {p.open_count || 0} · {timeAgo(p.updated_at)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {filtered.length > perPage && (
                <div className="px-5 py-3 flex items-center justify-between text-[12.5px] text-[#5F6677] bg-[#FAFBFF]">
                  <span>
                    Page {page} of {totalPages} · {filtered.length} results
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      data-testid="page-prev"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="h-8 px-2 rounded-md border border-[#E6E8EE] bg-white disabled:opacity-40 hover:bg-[#F7F8FA] flex items-center gap-1"
                    >
                      <ChevronLeft size={14} /> Prev
                    </button>
                    <button
                      data-testid="page-next"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className="h-8 px-2 rounded-md border border-[#E6E8EE] bg-white disabled:opacity-40 hover:bg-[#F7F8FA] flex items-center gap-1"
                    >
                      Next <ChevronRight size={14} />
                    </button>
                  </div>
                </div>
              )}
            </section>
          )}
        </div>
      </div>

      {/* Right panel (>= 1280px) */}
      {!isLoading && !isEmpty && (
        <aside
          data-testid="right-panel"
          className="hidden xl:flex flex-col w-[220px] shrink-0 border-l border-[#E6E8EE] bg-white p-4 gap-6 sticky top-0 h-screen overflow-y-auto"
        >
          {/* Activity */}
          <div>
            <div className="text-[11px] tracking-[0.16em] uppercase font-semibold text-[#8A93A6] mb-3">
              Activity
            </div>
            <ul className="space-y-3">
              {activity.length === 0 && (
                <li className="text-[12.5px] text-[#8A93A6]">No activity yet.</li>
              )}
              {activity.map((e) => (
                <li key={e.id}>
                  <button
                    data-testid={`activity-${e.id}`}
                    onClick={() => navigate(`/proposals/${e.proposalId}`)}
                    className="w-full text-left flex items-start gap-2 group"
                  >
                    <span
                      className="mt-1 h-2 w-2 rounded-full shrink-0"
                      style={{
                        background:
                          e.type === "opened" ? "#2E7D3A" :
                          e.type === "sent" ? "#2D5FB1" :
                          "#8A93A6",
                      }}
                    />
                    <span className="min-w-0">
                      <span className="block text-[12.5px] text-[#0B1736] group-hover:underline leading-snug">
                        {e.text}
                      </span>
                      {e.sub && <span className="block text-[11.5px] text-[#8A93A6] truncate">{e.sub}</span>}
                      <span className="block text-[11px] text-[#8A93A6] mt-0.5">{timeAgo(e.at)}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Week snapshot */}
          {stats && (
            <div>
              <div className="text-[11px] tracking-[0.16em] uppercase font-semibold text-[#8A93A6] mb-3">
                This week
              </div>
              <div className="space-y-3">
                <MiniStat label="Created" value={stats.createdThisWeek} />
                <MiniStat label="Sent" value={stats.sentThisMonth} />
                <MiniStat label="Opened" value={stats.spark.reduce((s, x) => s + x, 0)} />
              </div>
              <div className="mt-4">
                <div className="flex items-end justify-between text-[10.5px] text-[#8A93A6]">
                  <span>Last week</span>
                  <span>This week</span>
                </div>
                <div className="mt-1 flex h-2 rounded-full overflow-hidden bg-[#EDEFF7]">
                  <span
                    className="h-full"
                    style={{
                      width: `${Math.min(100, (stats.createdThisWeek / Math.max(1, stats.total)) * 100)}%`,
                      background: "#011B90",
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </aside>
      )}

      {followUp && (
        <FollowUpModal
          proposal={followUp}
          onClose={() => setFollowUp(null)}
          onSent={load}
          senderFirstName={userName}
        />
      )}
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[12.5px] text-[#5F6677]">{label}</span>
      <span className="text-[14px] font-semibold text-[#0B1736]">{value}</span>
    </div>
  );
}

function PipelineEmpty({ onNew }) {
  return (
    <div data-testid="pipeline-empty" className="p-12 text-center">
      <div
        className="mx-auto h-12 w-12 rounded-full grid place-items-center mb-4"
        style={{ background: "rgba(1, 27, 144,0.10)", color: "#011670" }}
      >
        <FileText size={18} />
      </div>
      <h3 className="text-[16px] font-semibold text-[#0B1736]">No proposals yet.</h3>
      <p className="text-[13px] text-[#5F6677] mt-1 max-w-md mx-auto">
        Import a Fireflies transcript or paste one manually to generate your first branded proposal.
      </p>
      <div className="mt-5 flex items-center justify-center gap-2">
        <button
          data-testid="empty-fireflies-btn"
          className="h-9 px-4 rounded-lg border border-[#E6E8EE] text-[13px] text-[#5F6677] hover:border-[#0B1736] hover:text-[#0B1736] transition-colors"
          onClick={() => toast("Fireflies import coming soon", { description: "Connect your account in Integrations." })}
        >
          Import from Fireflies
        </button>
        <button
          data-testid="empty-paste-btn"
          onClick={onNew}
          className="qw-btn-primary h-9 px-4 text-[13px]"
        >
          Paste transcript
        </button>
      </div>
    </div>
  );
}
